import React, { useState } from 'react';
import { MessageSquare, X, Send, Sparkles, Minimize2, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatBot } from '../hooks/useChatBot';
import { Button } from './ui/Button';
export function ChatBot() {
  const {
    isOpen,
    toggleChat,
    messages,
    sendMessage,
    isTyping,
    messagesEndRef
  } = useChatBot();
  const [inputValue, setInputValue] = useState('');
  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };
  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };
  return <>
      {/* Trigger Button */}
      <AnimatePresence data-id="element-10">
        {!isOpen && <motion.button initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0,
        opacity: 0
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={toggleChat} className="fixed bottom-6 right-6 z-40 flex items-center gap-3 px-6 py-4 bg-brand-navy text-white rounded-full shadow-xl hover:bg-brand-navy/90 transition-colors group" aria-label="Open chat" data-id="element-11">
            <span className="font-medium" data-id="element-12">Talk to Your Productivity Guide</span>
            <div className="w-10 h-10 bg-brand-gold rounded-full flex items-center justify-center text-brand-navy" data-id="element-13">
              <MessageSquare size={20} fill="currentColor" data-id="element-14" />
            </div>
          </motion.button>}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence data-id="element-15">
        {isOpen && <motion.div initial={{
        y: 20,
        opacity: 0,
        scale: 0.95
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 20,
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="fixed bottom-4 right-4 z-50 w-[95vw] md:w-[400px] h-[80vh] md:h-[600px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" data-id="element-16">
            {/* Header */}
            <div className="bg-brand-navy p-4 flex items-center justify-between shrink-0" data-id="element-17">
              <div className="flex items-center gap-3" data-id="element-18">
                <div className="relative" data-id="element-19">
                  <div className="w-10 h-10 bg-brand-gold rounded-full flex items-center justify-center text-brand-navy text-lg" data-id="element-20">
                    ⚡
                  </div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-brand-navy" data-id="element-21"></div>
                </div>
                <div data-id="element-22">
                  <h3 className="font-bold text-white text-sm" data-id="element-23">
                    Productivity Guide
                  </h3>
                  <p className="text-brand-gold text-xs" data-id="element-24">AI Mentor • Online</p>
                </div>
              </div>
              <button onClick={toggleChat} className="text-gray-400 hover:text-white transition-colors p-1" aria-label="Close chat" data-id="element-25">
                <Minimize2 size={20} data-id="element-26" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-gray-50 scrollbar-hide" data-id="element-27">
              {messages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-28">
                  <div className={`
                      max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm
                      ${msg.sender === 'user' ? 'bg-brand-navy text-white rounded-br-none' : 'bg-white text-brand-slate border border-gray-100 rounded-bl-none'}
                    `} data-id="element-29">
                    {msg.text}
                  </div>

                  {/* Article Link */}
                  {msg.articleLink && msg.sender === 'bot' && <a href={msg.articleLink.url} target="_blank" rel="noopener noreferrer" className="mt-2 max-w-[85%] flex items-center gap-2 px-4 py-2.5 bg-brand-navy/5 border border-brand-navy/10 rounded-xl text-xs hover:bg-brand-navy/10 hover:border-brand-navy/20 transition-colors group" data-id="element-30">
                      <BookOpen size={14} className="text-brand-gold shrink-0" data-id="element-31" />
                      <span className="text-brand-navy font-medium group-hover:text-brand-gold transition-colors truncate" data-id="element-32">
                        📖 Read more: {msg.articleLink.title}
                      </span>
                    </a>}

                  {/* Suggestions Chips */}
                  {msg.suggestions && msg.suggestions.length > 0 && <div className="mt-3 flex flex-wrap gap-2 max-w-[90%]" data-id="element-33">
                      {msg.suggestions.map((suggestion, idx) => <button key={idx} onClick={() => handleSuggestionClick(suggestion)} className="text-xs bg-white border border-brand-gold/30 text-brand-navy px-3 py-1.5 rounded-full hover:bg-brand-gold/10 hover:border-brand-gold transition-colors text-left" data-id="element-34">
                          {suggestion}
                        </button>)}
                    </div>}
                </div>)}

              {isTyping && <div className="flex items-start" data-id="element-35">
                  <div className="bg-white border border-gray-100 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm" data-id="element-36">
                    <div className="flex gap-1" data-id="element-37">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-38"></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-39"></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-40"></span>
                    </div>
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-41" />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100 shrink-0" data-id="element-42">
              <form onSubmit={handleSend} className="relative" data-id="element-43">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Ask about focus, habits, or routines..." className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold/50 text-sm text-brand-navy placeholder:text-gray-400" data-id="element-44" />
                <button type="submit" disabled={!inputValue.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-brand-navy hover:text-brand-gold disabled:opacity-30 disabled:hover:text-brand-navy transition-colors" data-id="element-45">
                  <Send size={18} data-id="element-46" />
                </button>
              </form>
              <div className="text-center mt-2" data-id="element-47">
                <p className="text-[10px] text-gray-400" data-id="element-48">
                  AI can make mistakes. Check important info.
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}